import React, { memo, useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ColumnConfig } from '@/components/tables/optimized/types';

interface VirtualizedTableProps {
  data: any[];
  columns: ColumnConfig[];
  height?: number;
  itemHeight?: number;
  onRowClick?: (item: any) => void;
  className?: string;
}

// Memoized row component to prevent unnecessary re-renders
const TableRowComponent = memo(({ index, style, data }: any) => {
  const { items, columns, onRowClick } = data;
  const item = items[index];

  const handleRowClick = useCallback(() => {
    if (onRowClick) {
      onRowClick(item);
    }
  }, [item, onRowClick]);

  return (
    <div style={style}>
      <TableRow 
        className="border-b hover:bg-muted/50 cursor-pointer"
        onClick={handleRowClick}
      >
        {columns.map((column: ColumnConfig) => (
          <TableCell 
            key={column.key} 
            className="px-4 py-2"
            style={{ 
              width: column.width || 'auto',
              minWidth: column.width || 'auto',
              maxWidth: column.width || 'auto',
            }}
          >
            {column.render ? column.render(item[column.key], item) : item[column.key]}
          </TableCell>
        ))}
      </TableRow>
    </div>
  );
});

TableRowComponent.displayName = 'TableRowComponent';

export const VirtualizedTable = memo<VirtualizedTableProps>(({
  data,
  columns,
  height = 400,
  itemHeight = 48,
  onRowClick,
  className = ''
}) => {
  // Memoize table data to prevent unnecessary re-renders
  const tableData = useMemo(() => ({
    items: data,
    columns,
    onRowClick
  }), [data, columns, onRowClick]);

  // Memoize column widths
  const totalWidth = useMemo(() => {
    return columns.reduce((total, col) => {
      return total + (col.width ? parseInt(col.width.toString()) : 150);
    }, 0);
  }, [columns]);

  return (
    <div className={`border rounded-md ${className}`}>
      {/* Header */}
      <div className="border-b">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead 
                  key={column.key}
                  style={{ 
                    width: column.width || 'auto',
                    minWidth: column.width || 'auto',
                    maxWidth: column.width || 'auto',
                  }}
                  className="px-4 py-2 font-medium"
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        </Table>
      </div>

      {/* Virtualized Body */}
      <div style={{ height }}>
        {data.length > 0 ? (
          <List
            height={height}
            itemCount={data.length}
            itemSize={itemHeight}
            itemData={tableData}
            width="100%"
          >
            {TableRowComponent}
          </List>
        ) : (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            No data available
          </div>
        )}
      </div>

      {/* Footer with item count */}
      <div className="px-4 py-2 text-sm text-muted-foreground border-t">
        Showing {data.length} items
      </div>
    </div>
  );
});

VirtualizedTable.displayName = 'VirtualizedTable';